/**
 * Search and filters for the Co-Living rooms page (Co-Living redesign, Slice 2).
 *
 * One control bar rather than a bare input, following the shape the Einheiten
 * page settled on (AdminApartmentsPage.js: search glyph, input, hairline
 * divider, selects) so the two sibling surfaces read as one product.
 *
 * WHAT THE PLACEHOLDER MAY PROMISE. It says exactly what the predicate does,
 * and no more. Until UI1 that was unit fields only — short id, address, postal
 * code, city — so the placeholder named those and deliberately not rooms. UI1
 * widened the predicate to room names, so "Zimmer" may now be named too.
 *
 * It still does NOT say "Liegenschaft". The page groups by UNIT, and property
 * is a filter rather than a level it renders (see `CoLivingUnitRow`); offering
 * to search a level that is not there would be the same broken promise in a
 * new place.
 *
 * The target design also shows a currency select and a "Mehr Filter" button.
 * Neither exists here: currency is not a dimension of any operational entity
 * in this system, and there are no further filter dimensions to put behind a
 * second control. A control that does nothing is worse than a missing one.
 */
import React from "react";

export const ALL_PROPERTIES = "all";
export const ALL_STATUSES = "all";

/** The three room states `getRoomOccupancyStatus` can answer with. */
export const STATUS_OPTIONS = [
  { value: "belegt", label: "Belegt" },
  { value: "reserviert", label: "Reserviert" },
  { value: "frei", label: "Frei" },
];

function SearchGlyph() {
  return (
    <svg
      className="h-[15px] w-[15px] shrink-0 text-muted-foreground"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" stroke="currentColor" strokeWidth="1.5" />
      <path d="M16.5 16.5 21 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function ChevronGlyph() {
  return (
    <svg
      className="pointer-events-none absolute right-0 top-1/2 h-[14px] w-[14px] -translate-y-1/2 text-muted-foreground"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * `appearance-none` drops the native arrow, so the chevron above has to put
 * one back — otherwise the reserved right padding is empty space and the
 * control reads as a label rather than as something you can open.
 */
function SelectShell({ children }) {
  return <span className="relative inline-flex items-center">{children}<ChevronGlyph /></span>;
}

const selectClasses =
  "max-w-[min(220px,45vw)] cursor-pointer appearance-none rounded-[8px] border-none bg-transparent py-1 pr-[18px] text-[12.5px] font-medium text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring";

export default function CoLivingToolbar({
  search,
  onSearchChange,
  propertyFilter,
  onPropertyFilterChange,
  propertyOptions = [],
  statusFilter,
  onStatusFilterChange,
  onReset = null,
}) {
  const hasActiveFilter =
    Boolean(String(search || "").trim()) ||
    propertyFilter !== ALL_PROPERTIES ||
    statusFilter !== ALL_STATUSES;

  return (
    <div className="flex flex-col gap-[10px] rounded-[12px] border border-input bg-card px-4 py-3 shadow-sm focus-within:border-ring md:flex-row md:items-center">
      <div className="flex min-w-0 flex-1 items-center gap-[10px]">
        <SearchGlyph />
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Nach Einheit, Adresse oder Zimmer suchen …"
          aria-label="Co-Living-Einheiten suchen"
          autoComplete="off"
          className="min-w-0 flex-1 border-none bg-transparent text-[13px] text-foreground outline-none placeholder:text-muted-foreground"
        />
      </div>

      <div className="hidden h-[22px] w-px shrink-0 bg-border md:block" />

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <SelectShell>
          <select
            value={propertyFilter}
            onChange={(e) => onPropertyFilterChange(e.target.value)}
            aria-label="Liegenschaft filtern"
            className={selectClasses}
          >
            <option value={ALL_PROPERTIES}>Alle Liegenschaften</option>
            {propertyOptions.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </SelectShell>

        <div className="hidden h-[22px] w-px shrink-0 bg-border sm:block" />

        <SelectShell>
          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            aria-label="Status filtern"
            className={selectClasses}
          >
            <option value={ALL_STATUSES}>Alle Status</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </SelectShell>

        {onReset && hasActiveFilter ? (
          <button
            type="button"
            onClick={onReset}
            className="rounded-[8px] border border-border bg-transparent px-2.5 py-1 text-[11.5px] font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Zurücksetzen
          </button>
        ) : null}
      </div>
    </div>
  );
}
